/**
 * Document Scanner Component
 * 
 * Nutzt native Dokumentenscanner von iOS/Android:
 * - iOS: Native "Dokumente scannen" Option im Datei-Dialog
 * - Android: Kamera mit Dokumentenerkennung oder Google Drive Scanner
 * 
 * Unterstützte Formate: PDF, JPEG, PNG, HEIC
 */

import React, { useState, useRef } from 'react';
import jsQR from 'jsqr';
import {
  Camera,
  X,
  Check,
  FileText,
  Loader2,
  ScanLine,
  Image as ImageIcon,
  Plus,
  Trash2,
  Upload,
  Smartphone
} from 'lucide-react';

interface DocumentScannerProps {
  onComplete: (pdfBlob: Blob, contractId: string | null) => void;
  onClose: () => void;
  contractId?: string;
}

interface ScannedPage {
  id: string;
  imageData: string; // Base64 data URL
  thumbnail: string; // Thumbnail for preview
  qrCode: string | null;
  isPdf: boolean;
  fileName: string;
}

export default function DocumentScanner({ onComplete, onClose, contractId: presetContractId }: DocumentScannerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [scannedPages, setScannedPages] = useState<ScannedPage[]>([]);
  const [detectedContractId, setDetectedContractId] = useState<string | null>(presetContractId || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // QR-Code aus ImageData extrahieren
  const extractQRCode = (imageData: ImageData): string | null => {
    try {
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code?.data) {
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidPattern.test(code.data)) {
          return code.data;
        }
      }
    } catch (e) {
      console.warn('QR scan error:', e);
    }
    return null;
  };

  // QR-Code aus Base64 Bild extrahieren
  const extractQRCodeFromImage = async (imageBase64: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 1000; // Limit size for performance
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        resolve(extractQRCode(imageData));
      };
      img.onerror = () => resolve(null);
      img.src = imageBase64;
    });
  };

  // Thumbnail erstellen
  const createThumbnail = async (imageBase64: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 150;
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        } else {
          resolve(imageBase64);
        }
      };
      img.onerror = () => resolve(imageBase64);
      img.src = imageBase64;
    });
  };

  // Datei zu Base64 konvertieren
  const fileToBase64 = (file: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // HEIC zu JPEG konvertieren
  const convertHeicToJpeg = async (file: File): Promise<Blob> => {
    try {
      const heic2any = (await import('heic2any')).default;
      const result = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.92
      });
      return Array.isArray(result) ? result[0] : result;
    } catch (e) {
      console.error('HEIC conversion failed:', e);
      throw new Error('HEIC-Konvertierung fehlgeschlagen');
    }
  };

  // Datei verarbeiten
  const processFile = async (file: File): Promise<ScannedPage | null> => {
    try {
      const fileName = file.name;
      const fileType = file.type.toLowerCase();
      const isHeic = fileType.includes('heic') || fileType.includes('heif') || 
                     fileName.toLowerCase().endsWith('.heic') || fileName.toLowerCase().endsWith('.heif');
      const isPdf = fileType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
      
      let imageBlob: Blob = file;
      let base64: string;
      let thumbnail: string;
      
      if (isHeic) {
        // HEIC zu JPEG konvertieren
        imageBlob = await convertHeicToJpeg(file);
        base64 = await fileToBase64(imageBlob);
        thumbnail = await createThumbnail(base64);
      } else if (isPdf) {
        // PDF: Base64 speichern, Standard-Thumbnail
        base64 = await fileToBase64(file);
        thumbnail = ''; // PDF Icon wird verwendet
      } else {
        // Normales Bild
        base64 = await fileToBase64(file);
        thumbnail = await createThumbnail(base64);
      }
      
      // QR-Code suchen (nur bei Bildern)
      let qrCode: string | null = null;
      if (!isPdf) {
        qrCode = await extractQRCodeFromImage(base64);
        if (qrCode && !detectedContractId) {
          setDetectedContractId(qrCode);
        }
      }
      
      return {
        id: `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        imageData: base64,
        thumbnail,
        qrCode,
        isPdf,
        fileName
      };
    } catch (error: any) {
      console.error('Error processing file:', error);
      setError(error.message || 'Fehler beim Verarbeiten der Datei');
      return null;
    }
  };

  // Datei-Upload Handler
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setLoadingFile(true);
    setError(null);
    
    try {
      for (const file of Array.from(files)) {
        const page = await processFile(file);
        if (page) {
          setScannedPages(prev => [...prev, page]);
        }
      }
    } catch (error: any) {
      console.error('Error processing files:', error);
      setError('Fehler beim Laden der Dateien');
    } finally {
      setLoadingFile(false);
      // Input zurücksetzen
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  // Seite löschen
  const removePage = (pageId: string) => {
    setScannedPages(prev => prev.filter(p => p.id !== pageId));
  };

  // PDF generieren und abschließen
  const finishScanning = async () => {
    if (scannedPages.length === 0) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const pdfBlob = await createPdfFromPages(scannedPages);
      
      const foundContractId = detectedContractId || 
        scannedPages.find(p => p.qrCode)?.qrCode || 
        null;
      
      onComplete(pdfBlob, foundContractId);
    } catch (error: any) {
      console.error('PDF creation error:', error);
      setError('Fehler beim Erstellen des PDFs');
    } finally {
      setIsProcessing(false);
    }
  };

  // PDF aus Seiten erstellen
  const createPdfFromPages = async (pages: ScannedPage[]): Promise<Blob> => {
    const { PDFDocument } = await import('pdf-lib');
    
    const pdfDoc = await PDFDocument.create();
    
    for (const page of pages) {
      const dataUrl = page.imageData;
      
      if (page.isPdf) {
        // PDF Seiten kopieren
        try {
          const base64Data = dataUrl.split(',')[1];
          const pdfBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
          const existingPdf = await PDFDocument.load(pdfBytes);
          const copiedPages = await pdfDoc.copyPages(existingPdf, existingPdf.getPageIndices());
          copiedPages.forEach(p => pdfDoc.addPage(p));
        } catch (e) {
          console.error('Error copying PDF pages:', e);
        }
      } else {
        // Bild einbetten
        try {
          const base64Data = dataUrl.split(',')[1];
          const imageBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
          
          let image;
          if (dataUrl.includes('image/png')) {
            image = await pdfDoc.embedPng(imageBytes);
          } else {
            image = await pdfDoc.embedJpg(imageBytes);
          }
          
          // A4 Seite erstellen
          const pdfPage = pdfDoc.addPage([595.28, 841.89]);
          const { width, height } = pdfPage.getSize();
          
          // Bild skalieren um auf Seite zu passen (mit Rand)
          const margin = 20;
          const scale = Math.min(
            (width - margin * 2) / image.width,
            (height - margin * 2) / image.height
          );
          
          const scaledWidth = image.width * scale;
          const scaledHeight = image.height * scale;
          
          // Zentriert zeichnen
          pdfPage.drawImage(image, {
            x: (width - scaledWidth) / 2,
            y: (height - scaledHeight) / 2,
            width: scaledWidth,
            height: scaledHeight
          });
        } catch (e) {
          console.error('Error embedding image:', e);
        }
      }
    }
    
    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  };

  // Erkennen ob iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-700">
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
        >
          <X className="h-6 w-6" />
        </button>
        
        <div className="text-white text-center">
          <h2 className="font-medium">Dokument scannen</h2>
          <p className="text-xs text-gray-400">
            {scannedPages.length} Seite(n)
            {detectedContractId && ' • QR erkannt'}
          </p>
        </div>
        
        <div className="w-10" /> {/* Spacer */}
      </div>

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*,.heic,.heif"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-4">
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm flex items-center">
            <X className="h-4 w-4 mr-2 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">×</button>
          </div>
        )}

        {/* Instructions */}
        {scannedPages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
              <ScanLine className="h-10 w-10 text-gray-500" />
            </div>
            <h3 className="text-white font-medium mb-2">Dokument hinzufügen</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-xs mx-auto">
              {isIOS 
                ? 'Wählen Sie "Dokumente scannen" für beste Qualität oder laden Sie existierende Dateien hoch.'
                : 'Fotografieren Sie das Dokument oder laden Sie existierende Dateien hoch.'
              }
            </p>
          </div>
        )}

        {/* Scanned Pages Grid */}
        {scannedPages.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {scannedPages.map((page, index) => (
              <div 
                key={page.id} 
                className="relative bg-gray-800 rounded-lg overflow-hidden aspect-[3/4] group"
              >
                {page.isPdf ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-700">
                    <FileText className="h-12 w-12 text-gray-400 mb-2" />
                    <span className="text-xs text-gray-400 px-2 text-center truncate w-full">
                      {page.fileName}
                    </span>
                  </div>
                ) : (
                  <img
                    src={page.thumbnail || page.imageData}
                    alt={`Seite ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                
                {/* Page Number */}
                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  {index + 1}
                </div>
                
                {/* QR Badge */}
                {page.qrCode && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded">
                    <Check className="h-3 w-3" />
                  </div>
                )}
                
                {/* Delete Button */}
                <button
                  onClick={() => removePage(page.id)}
                  className="absolute bottom-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            
            {/* Add More Card */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loadingFile}
              className="aspect-[3/4] border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:text-gray-300 hover:border-gray-400 transition-colors"
            >
              {loadingFile ? (
                <Loader2 className="h-8 w-8 animate-spin" />
              ) : (
                <>
                  <Plus className="h-8 w-8 mb-2" />
                  <span className="text-sm">Hinzufügen</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* QR Code Detection Info */}
        {detectedContractId && (
          <div className="p-3 bg-green-900/30 border border-green-700 rounded-lg mb-4">
            <div className="flex items-center text-green-400 text-sm">
              <Check className="h-4 w-4 mr-2" />
              <span>Vertrag erkannt: {detectedContractId.substring(0, 8)}...</span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Buttons */}
      <div className="bg-gray-800 p-4 border-t border-gray-700 space-y-3">
        {/* Primary Actions */}
        <div className="grid grid-cols-2 gap-3">
          {/* Scan/Camera Button */}
          <button
            onClick={() => {
              if (isIOS) {
                // iOS: Datei-Dialog öffnet native Scanner-Option
                fileInputRef.current?.click();
              } else {
                // Android: Kamera direkt öffnen
                cameraInputRef.current?.click();
              }
            }}
            disabled={loadingFile || isProcessing}
            className="flex items-center justify-center gap-2 py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {loadingFile ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                {isIOS ? <Smartphone className="h-5 w-5" /> : <Camera className="h-5 w-5" />}
                <span>{isIOS ? 'Scannen' : 'Fotografieren'}</span>
              </>
            )}
          </button>

          {/* Gallery/Files Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={loadingFile || isProcessing}
            className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium disabled:opacity-50"
          >
            <ImageIcon className="h-5 w-5" />
            <span>Galerie</span>
          </button>
        </div>

        {/* Finish Button */}
        {scannedPages.length > 0 && (
          <button
            onClick={finishScanning}
            disabled={isProcessing}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>PDF wird erstellt...</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                <span>Fertig ({scannedPages.length} Seite{scannedPages.length !== 1 ? 'n' : ''})</span>
              </>
            )}
          </button>
        )}

        {/* iOS Hint */}
        {isIOS && scannedPages.length === 0 && (
          <p className="text-xs text-gray-500 text-center">
            Tipp: Wählen Sie im Menü "Dokumente scannen" für automatische Kantenerkennung
          </p>
        )}
      </div>
    </div>
  );
}
